/* eslint-disable */
// PasswordStrengthMeter — live "will this password be accepted?" panel for auth
// screens (SetPasswordScreen today; signup flows tomorrow). Renders the 4-segment
// strength bar, the requirements checklist (incl. the LIVE HaveIBeenPwned breach
// check — the same database the server's leaked-password protection consults),
// the authoritative verdict banner, and a suggest-a-password affordance.
// Pure presentational + usePasswordStrength hook; parent owns the input state.
// Props: password, confirm, onVerdict({canSubmit, verdict}), onSuggest(pw).
import React, { useEffect } from 'react';
import styles from './PasswordStrengthMeter.module.css';
import { usePasswordStrength, suggestPassword } from '../../hooks/usePasswordStrength';

const Tick = () => (
  <svg viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true">
    <path d="M1.5 5.5l2.5 2.5 4.5-5.5" />
  </svg>
);

const LABELS = ['Password strength', 'Weak', 'Fair', 'Good', 'Strong'];
const VERDICT_COPY = {
  wait:       { cls: 'wait', text: 'Verifying against breach databases…' },
  leaked:     { cls: 'fail', text: '✗ This password appears in known data breaches and will be rejected — try the suggestion below.' },
  tooShort:   { cls: 'fail', text: '✗ Too short — at least 8 characters required.' },
  needConfirm:{ cls: 'wait', text: '✓ Password will be accepted — now confirm it below.' },
  willPass:   { cls: 'pass', text: '✓ This password will be accepted. You’re good to go.' },
};

export function PasswordStrengthMeter({ password, confirm, onVerdict, onSuggest }) {
  const s = usePasswordStrength(password, confirm);

  useEffect(() => {
    if (onVerdict) onVerdict({ canSubmit: s.canSubmit, verdict: s.verdict });
  }, [s.canSubmit, s.verdict]); // eslint-disable-line react-hooks/exhaustive-deps

  const effScore = password ? (s.leak === 'leaked' ? 1 : s.score) : 0;
  const checkCls = (state) => `${styles.check}${state ? ' ' + styles[state] : ''}`;
  const leakState = !password || password.length < 8 ? '' :
    s.leak === 'checking' ? 'pend' : s.leak === 'leaked' ? 'fail' : s.leak === 'clean' ? 'ok' : '';
  const leakText = s.leak === 'checking' ? 'Checking breach databases…'
    : s.leak === 'leaked' ? 'Found in leaked-password databases — will be rejected'
    : s.leak === 'unknown' ? 'Breach check unavailable — the server will verify'
    : 'Not found in known data breaches';
  const vc = VERDICT_COPY[s.verdict];

  return (
    <div className={styles.psmRoot}>
      <div className={styles.bar} aria-hidden="true">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`${styles.seg}${i <= effScore ? ' ' + styles['on' + effScore] : ''}`} />
        ))}
      </div>
      <div className={`${styles.label}${effScore ? ' ' + styles['l' + effScore] : ''}`}>
        {password ? LABELS[effScore] : LABELS[0]}
      </div>

      <div className={styles.checks}>
        <div className={checkCls(!password ? '' : s.lenOk ? 'ok' : 'fail')}>
          <span className={styles.ic}><Tick /></span>At least 8 characters
        </div>
        <div className={checkCls(!password ? '' : s.varietyOk ? 'ok' : 'fail')}>
          <span className={styles.ic}><Tick /></span>Mix of letters, numbers or symbols
        </div>
        <div className={checkCls(leakState)}>
          <span className={styles.ic}>{leakState !== 'pend' && <Tick />}</span>{leakText}
        </div>
        <div className={checkCls(!confirm ? '' : s.matchOk ? 'ok' : 'fail')}>
          <span className={styles.ic}><Tick /></span>Passwords match
        </div>
      </div>

      <div className={`${styles.verdict}${vc ? ` ${styles[vc.cls]} ${styles.show}` : ''}`} role="status">
        {vc ? vc.text : ''}
      </div>

      <div className={styles.suggestRow}>
        <button type="button" className={styles.suggestBtn} onClick={() => onSuggest && onSuggest(suggestPassword())}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
          </svg>
          Suggest a strong password
        </button>
      </div>
    </div>
  );
}
